import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icons.jsx';

// Captura uma foto do pedido usando a câmera do dispositivo (getUserMedia).
// Pede permissão de câmera ao abrir; em caso de erro/negação, oferece o
// fallback de escolher um arquivo. onCapture recebe um File JPEG.
export default function CameraCapture({ onCapture, onClose, onPickFile }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Seu navegador não permite acesso à câmera. Escolha um arquivo.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (e) {
        const denied = e?.name === 'NotAllowedError' || e?.name === 'SecurityError';
        setError(denied
          ? 'Permissão de câmera negada. Libere o acesso nas configurações do navegador ou escolha um arquivo.'
          : 'Não foi possível acessar a câmera. Escolha um arquivo.');
      }
    }

    start();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, []);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `pedido-${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture(file);
      },
      'image/jpeg',
      0.92,
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--black)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', color: 'var(--white)' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15 }}>Foto do pedido</div>
        <button onClick={onClose} aria-label="Fechar câmera" style={{ color: 'var(--white)', padding: 6 }}>
          <Icon.x size={22} color="var(--white)"/>
        </button>
      </div>

      <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {error ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--white)', maxWidth: 360 }}>
            <Icon.camera size={36} color="var(--white)" style={{ opacity: 0.6 }}/>
            <p style={{ marginTop: 12, fontSize: 14, lineHeight: 1.4 }}>{error}</p>
            <button className="btn btn-primary" onClick={onPickFile} style={{ marginTop: 18 }}>
              <Icon.file size={18} color="var(--white)"/> Escolher arquivo
            </button>
          </div>
        ) : (
          <video ref={videoRef} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'contain' }}/>
        )}
      </div>

      {!error && (
        <div style={{ padding: '18px 16px 28px', display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={capture}
            disabled={!ready}
            aria-label="Capturar foto"
            style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--white)', border: '4px solid var(--overlay-white-40)', opacity: ready ? 1 : 0.5, cursor: ready ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Icon.camera size={28} color="var(--brand-primary)"/>
          </button>
        </div>
      )}
    </div>
  );
}
